import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CPG_TIERS, isCpgProfile } from '../lib/planTiers';
import api from '../services/api';
import { billingApi, authApi } from '../services/api';
import {
  CreditCard,
  Check,
  Crown,
  Zap,
  Building2,
  Calendar,
  Download,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import clsx from 'clsx';

interface Subscription {
  id: string;
  status: string;
  tier: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

interface Invoice {
  id: string;
  number: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  created: string;
  hostedInvoiceUrl: string;
  invoicePdf: string;
}

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

const PRICING_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 200,
    description: 'Perfect for small cafes and food trucks',
    icon: Zap,
    features: [
      'POS on any tablet (iPad, Android)',
      'Kitchen display',
      'Basic inventory',
      'Staff management',
      'Email support',
    ],
  },
  {
    id: 'essentials',
    name: 'Essentials',
    price: 500,
    description: 'Great for growing restaurants',
    icon: Crown,
    popular: true,
    features: [
      'Kitchen display + station routing',
      'Full inventory with recipes',
      'Staff management',
      'Payroll & tip tracking',
      'Analytics dashboard',
      'Priority support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 1000,
    description: 'For multi-location operations',
    icon: Building2,
    features: [
      'Multi-location support',
      'Advanced inventory & waste tracking',
      'Full payroll with direct deposit',
      'Advanced analytics & forecasting',
      'API access',
      'Dedicated success manager',
    ],
  },
];

export default function BillingPage() {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch current restaurant data from API (fresh data with createdAt)
  const { data: userData } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await authApi.me();
      return response.data?.data;
    },
  });

  // Get current restaurant from API response
  const currentRestaurant = userData?.restaurants?.[0] || null;

  // Handle return from Stripe checkout (and Shopify charge approval)
  useEffect(() => {
    if (searchParams.get('shopify_approved') === '1') {
      api.post('/v1/billing/shopify/confirm')
        .finally(() => { window.location.href = '/billing?success=true'; });
      return;
    }
    if (searchParams.get('success') === 'true') {
      setSuccessMessage('Subscription activated successfully! Welcome to Mise.');
      // Clear the URL params
      setSearchParams({});
    } else if (searchParams.get('cancelled') === 'true') {
      // User cancelled - no message needed, just clear params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Calculate free trial status
  const getTrialInfo = () => {
    if (!currentRestaurant?.createdAt) return null;

    const createdAt = new Date(currentRestaurant.createdAt);
    const trialEndsAt = new Date(createdAt.getTime() + 14 * 24 * 60 * 60 * 1000);
    const today = new Date();
    const daysRemaining = differenceInDays(trialEndsAt, today);

    return {
      isInTrial: daysRemaining > 0,
      daysRemaining: Math.max(0, daysRemaining),
      trialEndsAt,
      subscriptionStatus: currentRestaurant.subscriptionStatus,
    };
  };

  const trialInfo = getTrialInfo();

  // Server truth about access — a comped/partner account is 'active' with no
  // Stripe subscription, and must never read as "trial ended, pay us".
  const { data: access } = useQuery({
    queryKey: ['billing-access'],
    queryFn: async () => (await billingApi.getAccess()).data?.data || null,
  });
  const { data: billingProfile } = useQuery({
    queryKey: ['billing-profile'],
    queryFn: async () => (await api.get('/v1/restaurants/current')).data?.data || null,
    staleTime: 60_000,
  });
  const isCpg = isCpgProfile(billingProfile?.settings);
  const planTiers = isCpg ? CPG_TIERS : PRICING_TIERS;

  // Fetch subscription status
  const { data: subscriptionData, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await billingApi.getSubscription();
      return response.data?.data;
    },
  });

  // Fetch payment methods
  const { data: paymentMethods, isLoading: pmLoading } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      const response = await billingApi.getPaymentMethods();
      return response.data?.data || [];
    },
  });

  // Fetch invoices
  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const response = await billingApi.getInvoices();
      return response.data?.data || [];
    },
  });

  // Open Stripe portal mutation
  const portalMutation = useMutation({
    mutationFn: () => billingApi.createPortalSession(window.location.href),
    onSuccess: (response) => {
      const url = response.data?.data?.url;
      if (url) {
        window.location.href = url;
      }
    },
    onError: () => {
      alert('Failed to open billing portal. Please try again.');
    },
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: () => billingApi.cancel(),
    onSuccess: () => {
      alert('Your subscription will be cancelled at the end of the billing period.');
      window.location.reload();
    },
  });

  // Reactivate subscription mutation
  const reactivateMutation = useMutation({
    mutationFn: () => billingApi.reactivate(),
    onSuccess: () => {
      alert('Your subscription has been reactivated!');
      window.location.reload();
    },
  });

  const subscription = subscriptionData?.subscription as Subscription | undefined;
  const hasSubscription = subscriptionData?.hasSubscription;
  const isActive = subscriptionData?.isActive;
  // Shopify App Store installs never touch Stripe (App Store req 1.2.1):
  // they subscribe through Shopify's Billing API — the charge is approved in
  // their Shopify admin and lands on their Shopify bill. The tag is written
  // at install-claim time.
  const viaShopify = Boolean((billingProfile?.settings as any)?.shopifyAppStore);
  const shopifyBilled = viaShopify && (billingProfile?.settings as any)?.billingVia === 'shopify' && access?.status === 'active';
  const isComped = !hasSubscription && access?.status === 'active' && !shopifyBilled;
  const manageOnWeb = viaShopify && !hasSubscription && !shopifyBilled;
  const shopifyCheckout = (tier: string) => {
    api.post('/v1/billing/shopify/checkout', { tier })
      .then((r) => { const url = r.data?.data?.url; if (url) window.location.href = url; })
      .catch((e) => alert(e?.response?.data?.error?.message || 'Could not start Shopify checkout. Is your Shopify store connected?'));
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-success flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Active</span>;
      case 'trialing':
        return <span className="badge badge-info flex items-center gap-1"><Zap className="w-3 h-3" /> Trial</span>;
      case 'past_due':
        return <span className="badge badge-warning flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Past Due</span>;
      case 'canceled':
        return <span className="badge badge-danger flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancelled</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const currentTier = PRICING_TIERS.find(t => t.id === subscription?.tier) || PRICING_TIERS[0];

  if (subLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Billing & Subscription</h1>
        <p className="text-slate-500">Manage your subscription and payment methods</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
          <p className="text-success-800">{successMessage}</p>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-auto text-success-600 hover:text-success-800"
          >
            &times;
          </button>
        </div>
      )}

      {/* Shopify App Store accounts: billing goes through Shopify, never Stripe */}
      {manageOnWeb && (
        <div className="card p-6 bg-slate-50 border-slate-200">
          <h2 className="text-lg font-semibold mb-1">Billing through Shopify</h2>
          <p className="text-sm text-slate-600">
            Because you installed Mise from the Shopify App Store, your subscription is billed
            through Shopify. Pick a plan below — you'll approve the charge in your Shopify admin,
            and it appears on your regular Shopify bill. Nothing is ever charged outside Shopify.
          </p>
        </div>
      )}

      {/* Trial Ending Soon Warning */}
      {!hasSubscription && !isComped && !shopifyBilled && trialInfo?.isInTrial && trialInfo.daysRemaining <= 3 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">Your trial is ending soon!</p>
              <p className="text-sm text-amber-700">
                {trialInfo.daysRemaining === 0
                  ? 'Your trial ends today.'
                  : trialInfo.daysRemaining === 1
                    ? 'Your trial ends tomorrow.'
                    : `Only ${trialInfo.daysRemaining} days left in your trial.`}
                {' '}Subscribe now to keep using Mise.
              </p>
            </div>
          </div>
          {manageOnWeb ? (
            <a href="#plans" className="btn btn-primary btn-sm">Choose a plan</a>
          ) : (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="btn btn-primary btn-sm"
            >
              Subscribe Now
            </button>
          )}
        </div>
      )}

      {/* Current Plan */}
      <div className="card p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold mb-1">Current Plan</h2>
            {hasSubscription ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <currentTier.icon className="w-8 h-8 text-primary-600" />
                  <div>
                    <p className="text-xl font-bold">{currentTier.name}</p>
                    <p className="text-slate-500">${currentTier.price}/month</p>
                  </div>
                  {getStatusBadge(subscription?.status || 'active')}
                </div>
                {subscription?.currentPeriodEnd && (
                  <p className="text-sm text-slate-500">
                    {subscription.cancelAtPeriodEnd ? (
                      <span className="text-amber-600">
                        Cancels on {format(new Date(subscription.currentPeriodEnd), 'MMMM d, yyyy')}
                      </span>
                    ) : (
                      <>Next billing date: {format(new Date(subscription.currentPeriodEnd), 'MMMM d, yyyy')}</>
                    )}
                  </p>
                )}
              </div>
            ) : shopifyBilled ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-success-600" />
                  <div>
                    <p className="text-xl font-bold">{(planTiers.find((t: any) => t.id === (billingProfile?.settings as any)?.shopifySubscription?.tier) as any)?.name || 'Mise'}</p>
                    <p className="text-slate-500">Billed through Shopify</p>
                  </div>
                  <span className="badge badge-success flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Active</span>
                </div>
                <p className="text-sm text-slate-500">
                  Your subscription is charged through your Shopify account. Manage or cancel it anytime from your Shopify admin.
                </p>
              </div>
            ) : trialInfo?.isInTrial ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold">Free Trial</p>
                    <p className="text-slate-500">Full access to all features</p>
                  </div>
                  <span className="badge badge-warning flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {trialInfo.daysRemaining} days left
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  Your trial ends on {format(trialInfo.trialEndsAt, 'MMMM d, yyyy')}. {manageOnWeb ? 'When you subscribe, billing goes through your Shopify account.' : 'Subscribe to continue using Mise.'}
                </p>
              </div>
            ) : (
              isComped ? (
                <div className="space-y-2">
                  <p className="text-xl font-bold">Partner plan</p>
                  <p className="text-sm text-slate-500">Full access, on the house — nothing to pay and nothing expiring. If you'd ever like a standard plan, the options are below.</p>
                </div>
              ) : (
              <div className="space-y-2">
                <p className="text-slate-600">No active subscription</p>
                <p className="text-sm text-slate-500">Your trial has ended. {manageOnWeb ? 'Choose a plan below — billed through your Shopify account.' : 'Choose a plan to continue using Mise.'}</p>
              </div>
              )
            )}
          </div>
          <div className="flex gap-2">
            {hasSubscription ? (
              <>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="btn btn-primary"
                >
                  Change Plan
                </button>
                <button
                  onClick={() => portalMutation.mutate()}
                  disabled={portalMutation.isPending}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  {portalMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  Manage in Stripe
                </button>
              </>
            ) : shopifyBilled ? null : manageOnWeb ? (
              <a href="#plans" className="btn btn-primary">
                Subscribe via Shopify
              </a>
            ) : (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="btn btn-primary"
              >
                Subscribe Now
              </button>
            )}
          </div>
        </div>

        {/* Cancel/Reactivate */}
        {hasSubscription && (
          <div className="mt-4 pt-4 border-t">
            {subscription?.cancelAtPeriodEnd ? (
              <button
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate subscription'}
              </button>
            ) : (
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to cancel? You will lose access at the end of your billing period.')) {
                    cancelMutation.mutate();
                  }
                }}
                disabled={cancelMutation.isPending}
                className="text-sm text-slate-500 hover:text-danger-600"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel subscription'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payment Methods - Only show for subscribers */}
      {hasSubscription && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Payment Methods</h2>
            <button
              onClick={() => portalMutation.mutate()}
              className="btn btn-secondary btn-sm"
            >
              Manage Cards
            </button>
          </div>

          {pmLoading ? (
            <div className="py-4 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : (paymentMethods as PaymentMethod[])?.length > 0 ? (
            <div className="space-y-3">
              {(paymentMethods as PaymentMethod[]).map((pm) => (
                <div
                  key={pm.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-slate-400" />
                    <div>
                      <p className="font-medium capitalize">{pm.brand} •••• {pm.last4}</p>
                      <p className="text-sm text-slate-500">
                        Expires {pm.expMonth}/{pm.expYear}
                      </p>
                    </div>
                  </div>
                  <span className="badge badge-success">Default</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500">
              <CreditCard className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No payment methods on file</p>
              <button
                onClick={() => portalMutation.mutate()}
                className="btn btn-primary btn-sm mt-3"
              >
                Add Payment Method
              </button>
            </div>
          )}
        </div>
      )}

      {/* Invoice History */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Invoice History</h2>

        {invoicesLoading ? (
          <div className="py-4 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : (invoices as Invoice[])?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="table-header">Invoice</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Status</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(invoices as Invoice[]).map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="table-cell font-medium">{invoice.number}</td>
                    <td className="table-cell">
                      {format(new Date(invoice.created), 'MMM d, yyyy')}
                    </td>
                    <td className="table-cell">{formatCurrency(invoice.amountPaid)}</td>
                    <td className="table-cell">
                      {invoice.status === 'paid' ? (
                        <span className="badge badge-success">Paid</span>
                      ) : (
                        <span className="badge badge-warning">{invoice.status}</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {invoice.hostedInvoiceUrl && (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        {invoice.invoicePdf && (
                          <a
                            href={invoice.invoicePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-500 hover:text-slate-700"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-slate-500">
            <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>{hasSubscription || isComped ? 'No invoices yet' : shopifyBilled || manageOnWeb ? 'Your invoices live in your Shopify admin.' : 'Invoices will appear here after you subscribe'}</p>
          </div>
        )}
      </div>

      {/* Pricing Plans for Trial Users */}
      {!hasSubscription && !shopifyBilled && (
        <div className="card p-6" id="plans">
          <h2 className="text-lg font-semibold mb-2">Available Plans</h2>
          <p className="text-slate-500 mb-6">{isComped ? 'For reference — your access doesn\u2019t depend on these.' : manageOnWeb ? 'Billed through your Shopify account — approve the charge in your Shopify admin.' : 'Choose a plan that fits your business'}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {planTiers.map((tier: any) => (
              <div
                key={tier.id}
                className={clsx(
                  'relative p-6 rounded-xl border-2 transition-all',
                  tier.popular ? 'border-primary-600 bg-primary-50' : 'border-slate-200'
                )}
              >
                {tier.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <tier.icon className="w-8 h-8 text-primary-600 mb-3" />
                <h3 className="text-lg font-bold">{tier.name}</h3>
                <p className="text-2xl font-bold mt-2">
                  ${tier.price}<span className="text-sm text-slate-500">/mo</span>
                </p>
                <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                <ul className="mt-4 space-y-2">
                  {tier.features.slice(0, 4).map((feature: string, i: number) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-success-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                  {tier.features.length > 4 && (
                    <li className="text-sm text-slate-500">+{tier.features.length - 4} more</li>
                  )}
                </ul>
                {!isComped && (
                <button
                  onClick={() => {
                    if (manageOnWeb) { shopifyCheckout(tier.id); return; }
                    setSelectedTier(tier.id);
                    setShowUpgradeModal(true);
                  }}
                  className={clsx(
                    'w-full mt-4 btn',
                    tier.popular ? 'btn-primary' : 'btn-secondary'
                  )}
                >
                  {manageOnWeb ? 'Subscribe via Shopify' : `Choose ${tier.name}`}
                </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && !manageOnWeb && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {hasSubscription ? 'Change Your Plan' : 'Choose Your Plan'}
              </h2>
              <p className="text-slate-500">
                {hasSubscription
                  ? 'Select a plan that fits your needs'
                  : trialInfo?.isInTrial
                    ? `Your free trial has ${trialInfo.daysRemaining} days remaining. Subscribe now to ensure uninterrupted access.`
                    : 'Select a plan to start using Mise'}
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {planTiers.map((tier: any) => (
                  <div
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={clsx(
                      'relative p-6 rounded-xl border-2 cursor-pointer transition-all',
                      selectedTier === tier.id
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-slate-200 hover:border-slate-300',
                      tier.popular && 'ring-2 ring-primary-600'
                    )}
                  >
                    {tier.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}
                    <tier.icon className="w-8 h-8 text-primary-600 mb-3" />
                    <h3 className="text-lg font-bold">{tier.name}</h3>
                    <p className="text-2xl font-bold mt-2">
                      ${tier.price}<span className="text-sm text-slate-500">/mo</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                    <ul className="mt-4 space-y-2">
                      {tier.features.map((feature: string, i: number) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-success-600" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {subscription?.tier === tier.id && (
                      <span className="absolute top-4 right-4 badge badge-success">Current</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  setSelectedTier(null);
                }}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!selectedTier) return;

                  if (hasSubscription) {
                    // Change existing subscription
                    if (selectedTier !== subscription?.tier) {
                      billingApi.changeTier({ tier: selectedTier, interval: 'monthly' })
                        .then(() => {
                          alert('Plan changed successfully!');
                          window.location.reload();
                        })
                        .catch(() => alert('Failed to change plan. Please try again.'));
                    }
                  } else {
                    // Create new subscription (for trial users)
                    billingApi.createCheckout({
                      tier: selectedTier,
                      interval: 'monthly',
                      successUrl: window.location.origin + '/billing?success=true',
                      cancelUrl: window.location.origin + '/billing?cancelled=true',
                    })
                      .then((response) => {
                        const url = response.data?.data?.url;
                        if (url) {
                          window.location.href = url;
                        } else {
                          alert('Failed to create checkout session. Please try again.');
                        }
                      })
                      .catch(() => alert('Failed to create checkout. Please try again.'));
                  }
                  setShowUpgradeModal(false);
                }}
                disabled={!selectedTier || (hasSubscription && selectedTier === subscription?.tier)}
                className="btn btn-primary"
              >
                {hasSubscription
                  ? (selectedTier === subscription?.tier ? 'Current Plan' : 'Change Plan')
                  : 'Subscribe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
